import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useRelay } from '../../../hooks/useRelay';
import { applyAction, HEIST_CARD_BY_ID } from '../../../games/heist-city/heist-city';
import type { HeistCityState, HeistCityAction } from '../../../games/heist-city/heist-city';
import type { RoomView } from '../../../types/room';
import { loadMap } from './data/mapLoader';
import { GameMap } from './components';
import CharacterCard from './components/CharacterCard';
import TeamInfoPanel from './components/TeamInfoPanel';
import LoadoutModal from './components/LoadoutModal';
import GameSaveModal from './components/GameSaveModal';
import HandManagementCard from './components/HandManagementCard';
import HandViewModal from './components/HandViewModal';
import { getGame } from '../../../games/registry';
import type { CharacterToken, SavedGameState } from './types';

interface Props {
  slug: string;
  room: RoomView;
  myId: string;
  isLeader: boolean;
  initialState: HeistCityState | null;
  onFinish: () => void;
}

export default function HeistCityRoomGame({ slug, room, myId, isLeader, initialState, onFinish }: Props) {
  const { state, startRelay, sendAction } = useRelay<HeistCityState>(slug);
  const relayStartedRef = useRef(false);
  const [showLoadoutModal, setShowLoadoutModal] = useState<{ type: 'save' | 'load' | 'export'; playerNumber: 1 | 2 } | null>(null);
  const [showGameSaveModal, setShowGameSaveModal] = useState<'save' | 'load' | null>(null);
  const [handModalPlayerId, setHandModalPlayerId] = useState<string | null>(null);

  const myParticipant = room.participants.find(p => p.id === myId);
  const isSpectator = myParticipant?.type === 'spectator';

  const currentPlayerNumber = useMemo((): 1 | 2 | 'observer' => {
    if (!state) return 'observer';
    const idx = state.players.findIndex(p => p.id === myId);
    if (idx === 0) return 1;
    if (idx === 1) return 2;
    return 'observer';
  }, [state, myId]);

  const blueVP = useMemo(() =>
    state?.mapState.characters.filter(c => c.playerNumber === 1).reduce((sum, c) => sum + (c.victoryPoints ?? 0), 0) ?? 0,
    [state?.mapState.characters]
  );
  const redVP = useMemo(() =>
    state?.mapState.characters.filter(c => c.playerNumber === 2).reduce((sum, c) => sum + (c.victoryPoints ?? 0), 0) ?? 0,
    [state?.mapState.characters]
  );

  useEffect(() => {
    if (!isLeader || !initialState || relayStartedRef.current) return;
    relayStartedRef.current = true;
    startRelay('heist-city', initialState);
  }, [isLeader, initialState, startRelay]);

  if (!state) {
    return <div className="text-white p-10">Loading Heist City...</div>;
  }

  const player1Name = state.players.find(p => p.playerNumber === 1)?.name ?? 'Player 1';
  const player2Name = state.players.find(p => p.playerNumber === 2)?.name ?? 'Player 2';
  const heistCityRef = getGame('heist-city')?.referenceUrl;

  const dispatch = (action: HeistCityAction) => {
    sendAction(action, applyAction(state, action));
  };

  const handleCharacterUpdate = (characterId: string, updates: Partial<CharacterToken>) => {
    dispatch({ type: 'updateCharacter', playerId: myId, characterId, updates });
  };

  const handleLoadMap = (mapId: string) => {
    const p1Id = state.players.find(p => p.playerNumber === 1)?.id ?? 'player-1';
    const p2Id = state.players.find(p => p.playerNumber === 2)?.id ?? 'player-2';
    const newMap = loadMap(mapId, p1Id, p2Id);
    dispatch({ type: 'loadMap', playerId: myId, mapId, mapState: newMap, gridType: newMap.gridType });
  };

  const handleLoadGame = (savedGame: SavedGameState) => {
    dispatch({
      type: 'loadGame',
      playerId: myId,
      mapState: savedGame.mapState,
      gridType: savedGame.gridType,
      turnNumber: savedGame.turnNumber,
      alertModifier: savedGame.alertModifier,
    });
  };

  const handleApplyLoadout = (updatedTeamCharacters: CharacterToken[]) => {
    if (!showLoadoutModal) return;
    const playerNumber = showLoadoutModal.playerNumber;
    const updatedMapState = {
      ...state.mapState,
      characters: state.mapState.characters.map(char => {
        if (char.playerNumber !== playerNumber) return char;
        const updated = updatedTeamCharacters.find(tc => tc.role === char.role);
        return updated ?? char;
      }),
    };
    dispatch({ type: 'updateMapState', playerId: myId, mapState: updatedMapState });
  };

  return (
    <div className="heist-city-game p-6 bg-gray-950 min-h-screen">
      <div className="max-w-[1550px] mx-auto">
        <div className="flex items-baseline gap-4 mb-4">
          <h1 className="text-3xl font-bold text-white">Heist City</h1>
          {heistCityRef && (
            <a href={heistCityRef} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-400 hover:text-blue-300">
              📖 Rules reference ↗
            </a>
          )}
        </div>

        <GameMap
          mapState={state.mapState}
          onMapStateChange={(mapState) => dispatch({ type: 'updateMapState', playerId: myId, mapState })}
          onSelectionChange={(characterId) => dispatch({ type: 'updateSelection', playerId: myId, characterId })}
          readOnly={isSpectator}
          gridType={state.gridType}
          onDiceRoll={(dice1, dice2, total) => dispatch({ type: 'rollDice', playerId: myId, dice1, dice2, total })}
          lastDiceRoll={state.lastDiceRoll}
          logEntries={state.logEntries}
          playerSelections={state.playerSelections}
          currentPlayerId={myId}
          currentPlayerNumber={currentPlayerNumber}
          onExhaustToggle={(characterId) => {
            const char = state.mapState.characters.find(c => c.id === characterId);
            if (char) handleCharacterUpdate(characterId, { exhausted: !char.exhausted });
          }}
          onActionUpdate={(characterId, actions) => handleCharacterUpdate(characterId, { actions })}
          onLoadMap={handleLoadMap}
          sharedRulerState={state.rulerState}
          onRulerUpdate={(start, end) => dispatch({ type: 'updateRuler', playerId: myId, start, end })}
          gameInfo={{ turnNumber: state.turnNumber, blueVictoryPoints: blueVP, redVictoryPoints: redVP }}
          onGameInfoChange={() => {}}
          alertModifier={state.alertModifier}
          onAlertModifierChange={(alertModifier) => dispatch({ type: 'updateAlert', playerId: myId, alertModifier })}
          onSaveGame={() => setShowGameSaveModal('save')}
          onLoadGame={() => setShowGameSaveModal('load')}
        />

        <div className="mt-6 bg-gray-800 p-4 rounded-lg">
          <h2 className="text-xl font-bold text-white mb-4">Game Info</h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-gray-300 text-sm font-semibold">Turn Number: {state.turnNumber}</span>
              <button
                onClick={() => dispatch({ type: 'endTurn', playerId: myId })}
                className="px-4 py-2 rounded-lg font-semibold text-white text-sm bg-green-600 hover:bg-green-700 active:scale-95 transition-all"
              >
                Pass Turn
              </button>
            </div>

            {/* Active played cards — one per player, visible to all */}
            <div className="p-3 bg-gray-900/60 rounded-lg">
              <p className="text-xs font-semibold text-gray-400 mb-2">Active Played Cards</p>
              <div className="space-y-1">
                {state.players.map(p => {
                  const hand = state.playerHands?.[p.id];
                  const card = hand?.activePlayedCard ? HEIST_CARD_BY_ID[hand.activePlayedCard] : null;
                  const pName = p.playerNumber === 1 ? player1Name : player2Name;
                  const nameColor = p.playerNumber === 1 ? 'text-blue-400' : 'text-red-400';
                  const deckColor = card?.deck === 'overt' ? 'text-red-300' : card?.deck === 'hidden' ? 'text-purple-300' : 'text-green-300';
                  return (
                    <div key={p.id} className="flex items-start gap-2 text-xs">
                      <span className={`font-semibold ${nameColor} shrink-0`}>{pName}:</span>
                      {card ? (
                        <span className={`font-semibold ${deckColor}`}>{card.name}
                          <span className="text-gray-400 font-normal ml-1">— {card.effect}</span>
                        </span>
                      ) : (
                        <span className="text-gray-500 italic">No card played</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <TeamInfoPanel
              playerNumber={1}
              playerName={player1Name}
              characters={state.mapState.characters.filter(c => c.playerNumber === 1)}
              onVPUpdate={(id, vp) => handleCharacterUpdate(id, { victoryPoints: vp })}
              onShowLoadoutModal={(type) => setShowLoadoutModal({ type, playerNumber: 1 })}
            />

            <div className="border-t border-gray-700 my-2" />

            <TeamInfoPanel
              playerNumber={2}
              playerName={player2Name}
              characters={state.mapState.characters.filter(c => c.playerNumber === 2)}
              onVPUpdate={(id, vp) => handleCharacterUpdate(id, { victoryPoints: vp })}
              onShowLoadoutModal={(type) => setShowLoadoutModal({ type, playerNumber: 2 })}
            />
          </div>
        </div>

        <div className="mt-6">
          <h2 className="text-2xl font-bold text-white mb-4">Character Cards</h2>

          <div className="mb-6">
            <div className="flex flex-wrap items-center gap-3 mb-3">
              <h3 className="text-lg font-bold text-blue-400">{player1Name} (Blue)</h3>
              {(() => {
                const p1 = state.players.find(p => p.playerNumber === 1);
                return p1 ? (
                  <HandManagementCard
                    hand={state.playerHands?.[p1.id] ?? { cards: [], activePlayedCard: null }}
                    onDealHand={() => dispatch({ type: 'dealHand', playerId: p1.id })}
                    onViewHand={() => setHandModalPlayerId(p1.id)}
                    readOnly={isSpectator}
                  />
                ) : null;
              })()}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-5 gap-4">
              {state.mapState.characters.filter(c => c.playerNumber === 1).map(character => (
                <CharacterCard
                  key={character.id}
                  character={character}
                  isOwnedByPlayer={character.playerId === myId}
                  onStatsUpdate={(id, stats) => handleCharacterUpdate(id, { stats })}
                  onStateUpdate={(id, newState) => handleCharacterUpdate(id,
                    newState === 'Unconscious' ? { state: newState, exhausted: true } : { state: newState }
                  )}
                  onEquipmentUpdate={(id, equipment) => handleCharacterUpdate(id, { equipment })}
                  onExperienceUpdate={(id, experience) => handleCharacterUpdate(id, { experience })}
                  onHiddenRotationUpdate={(id, hiddenRotation) => handleCharacterUpdate(id, { hiddenRotation })}
                  onDisguisePersonaUpdate={(id, disguisePersona) => handleCharacterUpdate(id, { disguisePersona })}
                />
              ))}
            </div>
          </div>

          <div>
            <div className="flex flex-wrap items-center gap-3 mb-3">
              <h3 className="text-lg font-bold text-red-400">{player2Name} (Red)</h3>
              {(() => {
                const p2 = state.players.find(p => p.playerNumber === 2);
                return p2 ? (
                  <HandManagementCard
                    hand={state.playerHands?.[p2.id] ?? { cards: [], activePlayedCard: null }}
                    onDealHand={() => dispatch({ type: 'dealHand', playerId: p2.id })}
                    onViewHand={() => setHandModalPlayerId(p2.id)}
                    readOnly={isSpectator}
                  />
                ) : null;
              })()}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-5 gap-4">
              {state.mapState.characters.filter(c => c.playerNumber === 2).map(character => (
                <CharacterCard
                  key={character.id}
                  character={character}
                  isOwnedByPlayer={character.playerId === myId}
                  onStatsUpdate={(id, stats) => handleCharacterUpdate(id, { stats })}
                  onStateUpdate={(id, newState) => handleCharacterUpdate(id,
                    newState === 'Unconscious' ? { state: newState, exhausted: true } : { state: newState }
                  )}
                  onEquipmentUpdate={(id, equipment) => handleCharacterUpdate(id, { equipment })}
                  onExperienceUpdate={(id, experience) => handleCharacterUpdate(id, { experience })}
                  onHiddenRotationUpdate={(id, hiddenRotation) => handleCharacterUpdate(id, { hiddenRotation })}
                  onDisguisePersonaUpdate={(id, disguisePersona) => handleCharacterUpdate(id, { disguisePersona })}
                />
              ))}
            </div>
          </div>
        </div>

        {handModalPlayerId && (() => {
          const modalPlayer = state.players.find(p => p.id === handModalPlayerId);
          const modalHand = state.playerHands?.[handModalPlayerId] ?? { cards: [], playedCards: [] };
          const modalName = modalPlayer?.playerNumber === 1 ? player1Name : player2Name;
          return (
            <HandViewModal
              playerName={modalName}
              playerNumber={modalPlayer?.playerNumber ?? 1}
              hand={modalHand}
              onPlayCard={(cardId) => dispatch({ type: 'playCard', playerId: handModalPlayerId, cardId })}
              onReorderHand={(cards) => dispatch({ type: 'reorderHand', playerId: handModalPlayerId, cards })}
              onClose={() => setHandModalPlayerId(null)}
              readOnly={isSpectator}
            />
          );
        })()}

        <LoadoutModal
          isOpen={showLoadoutModal}
          playerName={showLoadoutModal?.playerNumber === 1 ? player1Name : player2Name}
          characters={showLoadoutModal ? state.mapState.characters.filter(c => c.playerNumber === showLoadoutModal.playerNumber) : []}
          onApplyLoadout={handleApplyLoadout}
          onClose={() => setShowLoadoutModal(null)}
        />

        <GameSaveModal
          isOpen={showGameSaveModal}
          mapState={state.mapState}
          gridType={state.gridType}
          turnNumber={state.turnNumber}
          alertModifier={state.alertModifier}
          onLoadGame={handleLoadGame}
          onClose={() => setShowGameSaveModal(null)}
        />
      </div>
    </div>
  );
}
